/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import type { ImageType } from '@coursera-web/cds-labs';
import type { Maybe } from '__generated__/graphql-types';

import { Button, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import PlayIcon from 'bundles/ent-website/assets/PlayIcon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import type { EventingType } from 'bundles/unified-description-page-common/contexts/EventingContext';
import Overlay from 'bundles/xddp/components/shared/Overlay';

import _t from 'i18n!nls/unified-common';

export const IMAGE_HEIGHT = 375;
const IMAGE_WIDTH = 612;

const styles = {
  container: css`
    padding-left: var(--cds-spacing-50);
    border-radius: 8px;
    flex-grow: 1;
    display: flex;
    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  thumbnail: css`
    flex-basis: 50%;
  `,
  content: css`
    padding: var(--cds-spacing-400);
    display: flex;
    flex-basis: 50%;
    align-items: center;

    ${breakpoints.down('xs')} {
      margin: 0 var(--cds-spacing-100);
    }
  `,
  quoteIcon: css`
    position: absolute;
    top: 0;
    left: 0;
    line-height: 1;
  `,
  copy: css`
    position: relative;
    padding-left: 40px;

    ${breakpoints.down('sm')} {
      padding-left: var(--cds-spacing-400);
    }

    ${breakpoints.down('xs')} {
      padding-left: var(--cds-spacing-300);
    }
  `,
  authorName: css`
    padding: var(--cds-spacing-200) 0 var(--cds-spacing-50);
  `,
  videoPlaceholderContainer: css`
    position: relative;
    z-index: 3;
    height: ${IMAGE_HEIGHT}px;
    width: 100%;
    border-radius: 6px;
    overflow: hidden;
  `,
  imageContainer: css`
    // override styles added by LazyImgix
    > div {
      position: absolute;
      width: 100%;
      height: 100%;
      padding-bottom: unset;
      max-width: none;
    }

    img {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  playIcon: css`
    padding: 14px 14px;
    margin: 0 auto;
    position: absolute;
    z-index: 2;

    :hover,
    :active,
    :focus {
      background-color: initial;
    }

    height: 100%;
    width: 100%;
    max-width: unset;
  `,
  authorDetails: css`
    position: absolute;
    z-index: 2;
    bottom: 25px;
    left: 35px;
  `,
  videoOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: var(--cds-color-neutral-primary);
    opacity: 0.2;
    border-radius: 6px;
  `,
  // Default dimensions to render videos properly
  videoPlayerContainer: css`
    position: relative;
    padding-bottom: 56.25%;

    @media (min-width: 288px) {
      height: 128px;
    }

    @media (min-width: 576px) {
      height: 324px;
    }

    @media (min-width: 792px) {
      height: 445px;
    }

    @media (min-width: 1008px) {
      height: 567px;
    }

    @media (min-width: 1152px) {
      height: 648px;
    }
  `,
  video: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
};

export type CustomTestimonial = {
  authorName?: Maybe<string>;
  authorOrganization?: Maybe<string>;
  authorTitle?: Maybe<string>;
  eventingData?: Maybe<EventingType>;
  image?: Maybe<ImageType>;
  productName?: Maybe<string>;
  quote?: Maybe<string>;
  videoEmbedUrl?: Maybe<string>;
  videoPlaceholderImage?: Maybe<ImageType>;
  trackingIndex?: Maybe<number>;
  shouldUseImgix?: Maybe<boolean>;
  isInvertedTextColor?: boolean;
};

/**
 * @description A custom testimonial component that can be used in various places.
 */
const CustomTestimonialItem = ({
  authorName,
  authorOrganization,
  authorTitle,
  image,
  quote,
  videoEmbedUrl,
  videoPlaceholderImage,
  shouldUseImgix = true,
  productName,
  trackingIndex,
  eventingData,
  isInvertedTextColor,
}: CustomTestimonial) => {
  const [showVideoPlayback, setVideoPlayback] = useState(false);

  const thumbnailImage = videoEmbedUrl ? videoPlaceholderImage : image;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_testimonial_card',
    {
      testimonialCard: {
        author: authorName ?? '',
        text: quote ?? '',
        ...(trackingIndex !== undefined && trackingIndex !== null
          ? {
              index: trackingIndex,
            }
          : {}),
      },
      ...eventingData,
    }
  );

  if (!thumbnailImage) {
    return null;
  }

  return (
    <div css={styles.container} ref={visibilityTrackingRef}>
      <div css={styles.thumbnail}>
        <div css={styles.videoPlaceholderContainer}>
          <div css={styles.imageContainer}>
            {/* Image is decorative - quote and author info provide all meaningful content */}
            {shouldUseImgix ? (
              <LazyImgix
                src={thumbnailImage.url || ''}
                alt=""
                role="presentation"
                maxHeight={IMAGE_HEIGHT}
                maxWidth={IMAGE_WIDTH}
                style={{ maxWidth: '100%' }}
                imgParams={{ fit: 'crop' }}
              />
            ) : (
              <img
                src={thumbnailImage.url || ''}
                alt=""
                role="presentation"
                height={IMAGE_HEIGHT}
                width={IMAGE_WIDTH}
              />
            )}
          </div>
          {videoEmbedUrl && (
            <React.Fragment>
              <Button
                variant="ghost"
                component={TrackedButton}
                trackingName="testimonial_video_play"
                aria-label={_t('Play now')}
                onClick={() => setVideoPlayback(!showVideoPlayback)}
                withVisibilityTracking={false}
                requireFullyVisible={false}
                css={styles.playIcon}
                tabindex={-1}
              >
                <PlayIcon aria-hidden="true" />
              </Button>
              <div css={styles.videoOverlay} />
            </React.Fragment>
          )}
        </div>
        {showVideoPlayback && videoEmbedUrl && (
          <Overlay handleClose={() => setVideoPlayback(!showVideoPlayback)} isOpen={showVideoPlayback}>
            <div css={styles.videoPlayerContainer}>
              <iframe
                title={_t('Embedded Video')}
                width="100%"
                height="100%"
                src={videoEmbedUrl}
                frameBorder="0"
                allowFullScreen
                allow="autoplay; encrypted-media"
                css={styles.video}
              />
            </div>
          </Overlay>
        )}
      </div>
      <div css={styles.content}>
        <div css={styles.copy}>
          <Typography
            aria-hidden="true"
            variant="d1"
            css={styles.quoteIcon}
            component="p"
            color={isInvertedTextColor ? 'invertBody' : 'inherit'}
          >
            &ldquo;
          </Typography>
          <Typography variant="h2semibold" component="p" color={isInvertedTextColor ? 'invertBody' : 'inherit'}>
            {quote}
          </Typography>
          <Typography2
            variant="subtitleMedium"
            component="div"
            css={styles.authorName}
            color={isInvertedTextColor ? 'invertBody' : 'inherit'}
          >
            {authorName}
          </Typography2>
          <Typography2 component="p" color={isInvertedTextColor ? 'invertBody' : 'supportText'}>
            {authorOrganization
              ? _t('#{authorTitle} at #{authorOrganization}', { authorTitle, authorOrganization })
              : authorTitle}
          </Typography2>
          {productName && (
            <Typography2 component="p" color={isInvertedTextColor ? 'invertBody' : 'supportText'}>
              {productName}
            </Typography2>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTestimonialItem;
